import React from 'react';
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Zap, ShieldCheck, TrendingDown, Info, AlertCircle, Repeat, PlayCircle } from "lucide-react";
import { StrategyPresets } from './dca/settings/StrategyPresets';
import { PositionType } from '../types';
import { EXCHANGE_LIST, getSymbols, getCurrency, getMinNotionalWarning, getEnabledExchanges } from './dca/ExchangeUIConfigs';

interface SimpleSettingsProps {
  selectedSymbol: string;
  setSelectedSymbol: (val: string) => void;
  positionType: PositionType;
  setPositionType: (val: PositionType) => void;
  leverage: number;
  setLeverage: (val: number) => void;
  entryCount: number;
  setEntryCount: (val: number) => void;
  entryMultiplier: number;
  setEntryMultiplier: (val: number) => void;
  entryInterval: number;
  setEntryInterval: (val: number) => void;
  takeProfit: number;
  setTakeProfit: (val: number) => void;
  repeatExecution: boolean;
  setRepeatExecution: (val: boolean) => void;
  repeatType: string;
  setRepeatType: (val: string) => void;
  stopLoss: boolean;
  setStopLoss: (val: boolean) => void;
  stopLossValue: number;
  setStopLossValue: (val: number) => void;
  stopBotOnSL: boolean;
  setStopBotOnSL: (val: boolean) => void;
  predictedTotalOpenAmount: number;
  botName: string;
  setBotName: (val: string) => void;
  baseQty: number;
  setBaseQty: (val: number) => void;
  feePct: number;
  setFeePct: (val: number) => void;
  limitFeePct: number;
  setLimitFeePct: (val: number) => void;
  balance: number;
  setBalance: (val: number) => void;
  useAllBalance: boolean;
  setUseAllBalance: (val: boolean) => void;
  candleInterval: string;
  setCandleInterval: (val: string) => void;
  entryType: 'percent' | 'fixed';
  setEntryType: (val: 'percent' | 'fixed') => void;
  entryAmount: number;
  setEntryAmount: (val: number) => void;
  isLive: boolean;
  setIsLive: (val: boolean) => void;
  principalAmount: number;
  setPrincipalAmount: (val: number) => void;
  onStartBot: () => void;
  onSave?: () => void;
  slStrategy: 'realtime' | 'candle_close';
  setSlStrategy: (val: 'realtime' | 'candle_close') => void;
  is_testnet?: boolean;
  testnet_min_notional?: number;
  useTestnet: boolean;
  setUseTestnet: (val: boolean) => void;
  useProfitOffsetting: boolean;
  setUseProfitOffsetting: (val: boolean) => void;
  profitOffsetPct: number;
  setProfitOffsetPct: (val: number) => void;
  useLimitTp?: boolean;
  setUseLimitTp?: (val: boolean) => void;
  pullbackPct?: number;
  setPullbackPct?: (val: number) => void;
  exchange?: string;
  setExchange?: (val: string) => void;
  suspendedSides: string[];
  setSuspendedSides: (val: string[]) => void;
  isEdit?: boolean;
  disable_all_testnet?: boolean;
  enabled_exchanges?: Record<string, boolean>;
  marginMode: 'crossed' | 'isolated' | 'cross';
  setMarginMode: (val: 'crossed' | 'isolated' | 'cross') => void;
  immediateReentry: boolean;
  setImmediateReentry: (val: boolean) => void;
  use1hTrendFilter?: boolean;
  setUse1hTrendFilter?: (val: boolean) => void;
  isRunning?: boolean;
  applyPreset: (type: 'hybrid' | 'martingale' | 'scalping') => void;
}

export const SimpleSettings: React.FC<SimpleSettingsProps> = ({
  selectedSymbol, setSelectedSymbol,
  positionType, setPositionType,
  leverage, setLeverage,
  entryCount, setEntryCount,
  entryMultiplier, setEntryMultiplier,
  entryInterval, setEntryInterval,
  takeProfit, setTakeProfit,
  repeatExecution, setRepeatExecution,
  repeatType, setRepeatType,
  stopLoss, setStopLoss,
  stopLossValue, setStopLossValue,
  stopBotOnSL, setStopBotOnSL,
  predictedTotalOpenAmount,
  botName, setBotName,
  baseQty, setBaseQty,
  feePct, setFeePct,
  limitFeePct, setLimitFeePct,
  balance, setBalance,
  useAllBalance, setUseAllBalance,
  candleInterval, setCandleInterval,
  entryType, setEntryType,
  entryAmount, setEntryAmount,
  isLive,
  setIsLive,
  principalAmount,
  setPrincipalAmount,
  onStartBot,
  onSave,
  slStrategy,
  setSlStrategy,
  is_testnet,
  testnet_min_notional,
  useTestnet, setUseTestnet,
  useProfitOffsetting, setUseProfitOffsetting,
  profitOffsetPct, setProfitOffsetPct,
  useLimitTp, setUseLimitTp,
  pullbackPct, setPullbackPct,
  exchange, setExchange,
  suspendedSides, setSuspendedSides,
  isEdit,
  disable_all_testnet,
  enabled_exchanges,
  marginMode,
  setMarginMode,
  immediateReentry,
  setImmediateReentry,
  use1hTrendFilter,
  setUse1hTrendFilter,
  isRunning,
  applyPreset
}) => {
  const currency = getCurrency(selectedSymbol);
  const isInsufficientBalance = !useAllBalance && balance > 0 && balance < (predictedTotalOpenAmount / leverage);
  const baseEntryAmount = entryType === 'percent'
    ? (principalAmount > 0 ? principalAmount : (balance > 0 ? balance : 1)) * (baseQty / 100)
    : entryAmount;
  const firstEntryNotional = Number(baseEntryAmount || 0) * Number(leverage || 1);
  const minNotionalWarning = getMinNotionalWarning(exchange, isLive, useTestnet, firstEntryNotional, testnet_min_notional);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start transition-colors duration-300">
      <Card className="lg:col-span-8 bg-card shadow-sm border-border rounded-2xl overflow-hidden">
        <StrategyPresets applyPreset={applyPreset} isRunning={isRunning} />
        <CardHeader className="bg-muted/30 border-b border-border p-0">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-8 py-6">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">거래소 (Exchange)</Label>
                <Select value={exchange} onValueChange={setExchange} disabled={isEdit}>
                  <SelectTrigger className="h-8 w-[200px] text-xs font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {getEnabledExchanges(enabled_exchanges).map(ex => (
                      <SelectItem key={ex.value} value={ex.value}>{ex.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {exchange === 'okx' && (
                  <p className="text-[10px] font-bold text-amber-600 mt-1 animate-pulse px-1">
                    ⚠️ OKX는 현재 연동 테스트 중입니다. (시뮬레이션 권장)
                  </p>
                )}
                {exchange === 'bybit' && (
                  <p className="text-[10px] font-bold text-amber-600/80 px-1 mt-1 leading-tight flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    ⚠️ Bybit는 현재 연동 테스트 중입니다. (시뮬레이션 권장)
                  </p>
                )}
                {/* {exchange === 'bitget' && (
                  <p className="text-[10px] font-bold text-amber-600 mt-1 animate-pulse px-1">
                    ⚠️ Bitget는 현재 연동 테스트 중입니다. (시뮬레이션 권장)
                  </p>
                )} */}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">티커 심볼 (Symbol)</Label>
                <Select value={selectedSymbol} onValueChange={setSelectedSymbol} disabled={isEdit}>
                  <SelectTrigger className="h-8 w-[200px] text-xs font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
                    <SelectValue placeholder="종목 선택" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-[50vh] overflow-y-auto">
                    {getSymbols(exchange, useTestnet).map(sym => (
                      <SelectItem key={sym.value} value={sym.value}>{sym.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {/* <Badge variant="outline" className="text-[10px] font-bold py-0 h-5 bg-card border-border text-muted-foreground uppercase">Pro Trader Mode</Badge> */}
                <div className="flex flex-col gap-1.5 mt-1.5">
                  <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">거래 모드 (Trading Mode)</Label>
                  <Select value={isLive ? 'live' : 'paper'} onValueChange={(v) => setIsLive(v === 'live')}>
                    <SelectTrigger className="h-7 w-[130px] text-[10px] font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="paper">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          <span className="text-[10px] font-bold">시뮬레이션 (Paper)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="live">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                          <span className="text-[10px] font-bold">실매매 (Live)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(isLive && !disable_all_testnet && (exchange === 'binance' || exchange === 'okx' || exchange === 'bybit' || exchange === 'bitget')) && (
                  <div className="flex flex-col gap-1.5 mt-1.5">
                    <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">
                      접속 환경 ({exchange === 'binance' || exchange === 'bybit' ? 'Network' : 'Demo'})
                    </Label>
                    <Select value={useTestnet ? 'testnet' : 'mainnet'} onValueChange={(v) => setUseTestnet(v === 'testnet')} disabled={isEdit}>
                      <SelectTrigger className="h-7 w-[130px] text-[10px] font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm disabled:opacity-80">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="mainnet">
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="w-3 h-3 text-success" />
                            <span className="text-[10px] font-bold">메인넷 (Mainnet)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="testnet">
                          <div className="flex items-center gap-2">
                            <Zap className="w-3 h-3 text-amber-500" />
                            <span className="text-[10px] font-bold">
                              {exchange === 'binance' || exchange === 'bybit' ? '테스트넷 (Testnet)' : '데모 (Demo)'}
                            </span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
            <div className="flex bg-muted/50 p-1 rounded-lg gap-1 w-full sm:w-[200px]">
              <Button
                variant={positionType === 'LONG' ? "default" : "ghost"}
                onClick={() => setPositionType('LONG')}
                className={`flex-1 h-8 rounded-lg px-2 text-[11px] font-black transition-all ${positionType === 'LONG' ? 'bg-success hover:opacity-90 shadow-sm text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Long
              </Button>
              <Button
                variant={positionType === 'SHORT' ? "default" : "ghost"}
                onClick={() => setPositionType('SHORT')}
                className={`flex-1 h-8 rounded-lg px-2 text-[11px] font-black transition-all ${positionType === 'SHORT' ? 'bg-danger hover:opacity-90 shadow-sm text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Short
              </Button>
              <Button
                variant={positionType === 'BOTH' ? "default" : "ghost"}
                onClick={() => setPositionType('BOTH')}
                className={`flex-1 h-8 rounded-lg px-2 text-[11px] font-black transition-all ${positionType === 'BOTH' ? 'bg-primary hover:opacity-90 shadow-sm text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Both
              </Button>
            </div>
            {positionType === 'BOTH' && (
              <div className="flex items-center gap-4 px-2 py-1 bg-muted/30 rounded-lg animate-in fade-in slide-in-from-right-2 duration-300">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="enable-long"
                    checked={!suspendedSides.includes('long')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSuspendedSides(suspendedSides.filter(s => s !== 'long'));
                      } else {
                        setSuspendedSides([...suspendedSides, 'long']);
                      }
                    }}
                    className="border-success data-[state=checked]:bg-success data-[state=checked]:border-success"
                  />
                  <Label htmlFor="enable-long" className="text-[10px] font-black text-success uppercase cursor-pointer">Long 활성화</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="enable-short"
                    checked={!suspendedSides.includes('short')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSuspendedSides(suspendedSides.filter(s => s !== 'short'));
                      } else {
                        setSuspendedSides([...suspendedSides, 'short']);
                      }
                    }}
                    className="border-danger data-[state=checked]:bg-danger data-[state=checked]:border-danger"
                  />
                  <Label htmlFor="enable-short" className="text-[10px] font-black text-danger uppercase cursor-pointer">Short 활성화</Label>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div id='strategyName' className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">봇 이름</Label>
              <Input
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                placeholder="전략의 이름을 입력하세요"
                className="h-10 text-sm font-black border-border rounded-xl focus:ring-1 focus:ring-primary bg-background"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">전략 총 예산 (Total Budget)</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={principalAmount}
                  onChange={(e) => setPrincipalAmount(Number(e.target.value))}
                  className="h-10 text-sm font-black border-border rounded-xl pr-16 bg-background"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm uppercase">{currency}</span>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-tight ml-1">
                * 이 예산은 물타기(Scaling) 단계별 진입 수량을 계산하기 위한 기준 금액입니다.<br />
                * 간편 설정에서는 전체 잔고를 활용하여 매매를 진행하는 것으로 설정합니다.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">수수료 (Market %)</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={feePct}
                  step="0.001"
                  onChange={(e) => setFeePct(Number(e.target.value))}
                  className="h-10 text-sm font-black border-border rounded-xl pr-10 bg-background"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">%</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">수수료 (Limit %)</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={limitFeePct}
                  step="0.001"
                  onChange={(e) => setLimitFeePct(Number(e.target.value))}
                  className="h-10 text-sm font-black border-border rounded-xl pr-10 bg-background"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">%</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">캔들 간격 (Interval)</Label>
              <Select value={candleInterval} onValueChange={setCandleInterval}>
                <SelectTrigger className="h-10 text-sm font-black border-border rounded-xl focus:ring-1 focus:ring-primary bg-background">
                  <SelectValue placeholder="간격 선택" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="1m">1분봉 (1m)</SelectItem>
                  <SelectItem value="3m">3분봉 (3m)</SelectItem>
                  <SelectItem value="5m">5분봉 (5m)</SelectItem>
                  <SelectItem value="15m">15분봉 (15m)</SelectItem>
                  <SelectItem value="30m">30분봉 (30m)</SelectItem>
                  <SelectItem value="1h">1시간봉 (1h)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[9px] text-muted-foreground font-medium leading-tight mt-1 ml-1">지표 계산용이며, 매매 대응은 **3초마다 실시간**으로 이루어집니다.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <Label className="text-foreground font-bold text-xs">레버리지 (x{leverage})</Label>
                <div className="flex bg-muted rounded-lg p-0.5 h-7">
                  <Button
                    variant={marginMode === 'crossed' || marginMode === 'cross' ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setMarginMode('crossed')}
                    className={`px-2 h-full text-[9px] font-bold rounded-md ${marginMode === 'crossed' || marginMode === 'cross' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                  >
                    교차 (Cross)
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled
                    className="px-2 h-full text-[9px] font-bold rounded-md text-muted-foreground/30 cursor-not-allowed bg-transparent opacity-40"
                  >
                    격리 (Isolated)
                  </Button>
                </div>
              </div>
              <div className="pt-1">
                <Slider value={[leverage]} max={100} step={1} onValueChange={(val) => setLeverage(val[0])} className="my-2" disabled={isEdit} />
                <div className="flex justify-between text-[10px] text-muted-foreground font-bold px-1 uppercase tracking-tighter">
                  <span>1X</span><span>25X</span><span>50X</span><span>75X</span><span>100X</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border-t border-border pt-5">
            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">목표 익절 (ROE %)</Label>
              <div className="relative">
                <Input type="number" value={takeProfit} onChange={(e) => setTakeProfit(Number(e.target.value))} className="h-10 text-sm font-black border-border rounded-xl pr-10 bg-background" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-success text-sm">%</span>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-tight ml-1">평균 단가 대비 목표 수익률입니다.</p>

              <div className="flex items-center justify-between mt-3 px-1">
                <Label className="text-[10px] font-black text-muted-foreground uppercase tracking-tight">지정가 익절 사용 (Limit TP)</Label>
                <Checkbox
                  checked={useLimitTp}
                  onCheckedChange={(v) => setUseLimitTp?.(!!v)}
                  className="border-border"
                />
              </div>

              {useLimitTp && (
                <div className="mt-2 space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  <div className="flex justify-between items-center px-1">
                    <Label className="text-[9px] font-bold text-muted-foreground uppercase">되돌림 방어 (%)</Label>
                    <span className="text-[10px] font-black text-primary">{pullbackPct}%</span>
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={pullbackPct}
                    onChange={(e) => setPullbackPct?.(Number(e.target.value))}
                    className="h-8 text-[11px] font-black border-border rounded-lg bg-background focus:ring-1 focus:ring-primary"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <Label className="text-foreground font-bold text-xs">기본 증거금 (또는 1회차 사용자산)</Label>
                <div className="flex bg-muted rounded-md p-0.5 h-7">
                  <Button
                    variant={entryType === 'percent' ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setEntryType('percent')}
                    className={`px-2 h-full text-[10px] font-bold rounded ${entryType === 'percent' ? ' shadow-xs' : 'text-muted-foreground'}`}
                  >
                    %
                  </Button>
                  <Button
                    variant={entryType === 'fixed' ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setEntryType('fixed')}
                    className={`px-2 h-full text-[10px] font-bold rounded ${entryType === 'fixed' ? ' shadow-xs' : 'text-muted-foreground'}`}
                  >
                    $
                  </Button>
                </div>
              </div>
              <div className="relative">
                <Input
                  type="number"
                  value={entryType === 'percent' ? baseQty : entryAmount}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (entryType === 'percent') {
                      setBaseQty(val);
                    } else {
                      setEntryAmount(val);
                    }
                  }}

                  className="h-10 text-sm font-black border-border rounded-xl pr-16 bg-background"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">
                  {entryType === 'percent' ? '%' : currency}
                </span>
              </div>
              <div className="flex justify-between items-center px-1">
                <div className="flex flex-col gap-0.5">
                  <p className="text-[10px] text-muted-foreground font-medium leading-tight">레버리지 적용 전의 실제 투입 증거금(사용자산)입니다.</p>
                  {entryType === 'percent' && principalAmount > 0 && (
                    <p className="text-[10px] text-primary font-bold">
                      예상 진입 금액: {(principalAmount * (baseQty / 100)).toFixed(2)} {currency}
                    </p>
                  )}
                </div>
                <Badge variant="outline" className="text-[9px] font-bold py-0 h-4 border-primary/20 text-primary bg-primary/5">
                  증거금(Cost) × Leverage = 포지션 규모
                </Badge>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border-t border-border pt-5">
            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">물타기 간격 (%)</Label>
              <div className="relative">
                <Input type="number" value={entryInterval} onChange={(e) => setEntryInterval(Number(e.target.value))} step="0.01" className="h-10 text-sm font-black border-border rounded-xl pr-10 bg-background" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">%</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">수량 배수 (Multiplier)</Label>
              <div className="relative">
                <Input type="number" value={entryMultiplier} onChange={(e) => setEntryMultiplier(Number(e.target.value))} step="0.1" className="h-10 text-sm font-black border-border rounded-xl pr-10 bg-background" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">x</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border-t border-border pt-5 pb-2">
            <div className="space-y-2">
              <Label className="text-foreground font-bold ml-1 text-xs">최대 단계 (Scaling)</Label>
              <div className="relative">
                <Input type="number" value={entryCount} onChange={(e) => setEntryCount(Number(e.target.value))} className="h-10 text-sm font-black border-border rounded-xl pr-16 bg-background" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">Step</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between ml-1">
                <Label className="text-foreground font-bold text-xs">손절 (ROE %)</Label>
                <div className="flex items-center gap-2">
                  <Checkbox checked={stopLoss} onCheckedChange={(checked) => setStopLoss(!!checked)} id="sl-switch" className="border-border" />
                  <Label htmlFor="sl-switch" className="text-[10px] font-bold text-muted-foreground uppercase cursor-pointer">사용</Label>
                </div>
              </div>
              <div className="relative">
                <Input
                  type="number"
                  value={stopLossValue}
                  onChange={(e) => setStopLossValue(Number(e.target.value))}
                  disabled={!stopLoss}
                  className={`h-10 text-sm font-black border-border rounded-xl pr-10 bg-background ${!stopLoss ? 'opacity-50' : ''}`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-danger text-sm">%</span>
              </div>
              <p className="text-[9px] text-muted-foreground font-medium leading-tight mt-0.5 text-right w-full">물타기 포함 총 평단가 대비 손실률(PNL) 기준입니다.</p>

              {/* SL 전략 선택 (Simple 모드) - 손절 사용시에만 노출 */}
              {stopLoss && (
                <div className="flex flex-col mt-4 animate-in fade-in slide-in-from-top-1 duration-300">
                  <div className="grid grid-cols-2 bg-muted/50 p-1 rounded-xl gap-1 w-full border border-border/50">
                    <Button
                      variant={slStrategy === 'realtime' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSlStrategy('realtime')}
                      className={`h-8 rounded-lg px-2 text-[10px] font-bold transition-all ${slStrategy === 'realtime' ? 'bg-primary text-primary-foreground shadow-md' : 'text-muted-foreground'}`}
                    >
                      실시간
                    </Button>
                    <Button
                      variant={slStrategy === 'candle_close' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSlStrategy('candle_close')}
                      className={`h-8 rounded-lg px-2 text-[10px] font-bold transition-all ${slStrategy === 'candle_close' ? 'bg-success text-white shadow-md' : 'text-muted-foreground'}`}
                    >
                      봉 마감
                    </Button>
                  </div>
                  <div className="mt-2 text-center">
                    <p className="text-[9px] text-muted-foreground font-medium italic leading-tight">
                      💡 낮은 레버리지 추세 전략에는 봉 마감 대응을 추천합니다. 봉 마감은 캔들이 끝난 시점의 종가를 기준으로 판단합니다.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-5 border-t border-border space-y-4">
            {positionType === 'BOTH' && (
              <div className="flex items-center gap-2 bg-muted/30 p-3 rounded-xl border border-border mb-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <ShieldCheck className="w-4 h-4 text-primary fill-primary/20" />
                <div className="flex items-center justify-between flex-1">
                  <div className="flex items-center gap-2">
                    <Checkbox checked={useProfitOffsetting} onCheckedChange={(checked) => setUseProfitOffsetting(!!checked)} id="use-offset" className="border-border" />
                    <div className="flex flex-col">
                      <Label htmlFor="use-offset" className="text-xs font-bold cursor-pointer text-foreground">수익 상계 처리 (Profit Offsetting)</Label>
                      <p className="text-[9px] text-muted-foreground font-medium">익절 시 반대편 손실 포지션을 함께 정리합니다.</p>
                    </div>
                  </div>
                  {useProfitOffsetting && (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-1 duration-200">
                      <span className="text-[10px] font-bold text-muted-foreground">비율</span>
                      <div className="relative w-16">
                        <Input
                          type="number"
                          value={profitOffsetPct}
                          onChange={(e) => setProfitOffsetPct(Number(e.target.value))}
                          className="h-7 text-[10px] font-black pr-5 rounded-lg border-border bg-background"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-muted-foreground">%</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 bg-muted/30 p-3 rounded-xl border border-border mb-4">
              <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
              <div className="flex items-center gap-4 flex-1">
                <div className="flex items-center gap-2">
                  <Checkbox checked={repeatExecution} onCheckedChange={(checked) => setRepeatExecution(!!checked)} id="repeat" className="border-border" />
                  <Label htmlFor="repeat" className="text-xs font-bold cursor-pointer text-foreground flex items-center gap-1.5">
                    {repeatExecution ? <Repeat className="w-3.5 h-3.5 text-primary" /> : <PlayCircle className="w-3.5 h-3.5 text-muted-foreground" />}
                    {repeatExecution ? '반복 실행 (Cyclic)' : '1회만 실행 (Run Once)'}
                  </Label>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <Checkbox checked={stopBotOnSL} onCheckedChange={(checked) => setStopBotOnSL(!!checked)} id="stop-bot" className="border-border" />
                  <Label htmlFor="stop-bot" className="text-[10px] text-muted-foreground font-medium cursor-pointer">손절 시 봇 정지</Label>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-muted/30 p-3 rounded-xl border border-border mb-4 animate-in fade-in slide-in-from-top-1 duration-200">
              <div className="flex items-center gap-4 flex-1">
                <div className="flex items-center gap-3">
                  <Checkbox checked={immediateReentry} onCheckedChange={(checked) => setImmediateReentry(!!checked)} id="immediate-reentry-simple" className="border-border" />
                  <div className="flex flex-col">
                    <Label htmlFor="immediate-reentry-simple" className="text-xs font-bold cursor-pointer text-foreground flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />익절 후 즉시 재진입
                    </Label>
                    <p className="text-[9px] text-muted-foreground font-medium">익절 직후 지표 조건을 기다리지 않고 다음 사이클을 시작합니다.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className={`flex flex-col md:flex-row justify-between items-center gap-6 rounded-2xl p-5 border shadow-sm overflow-hidden relative transition-all duration-500 ${predictedTotalOpenAmount < (principalAmount * 0.95)
              ? 'bg-amber-500/5 border-amber-500/20 text-amber-900 shadow-inner'
              : predictedTotalOpenAmount > (principalAmount * 1.05)
                ? 'bg-danger/5 border-danger/20 text-danger shadow-inner'
                : 'bg-muted/30 border-border text-foreground'
              }`}>
              {minNotionalWarning && (
                <div className="bg-danger/10 border-b border-danger/20 p-4 -mx-5 -mt-5 mb-3 flex flex-col items-center justify-center text-center animate-in slide-in-from-top duration-300">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-danger animate-pulse" />
                    <p className="text-sm font-black text-danger/80">주문 제한 경고</p>
                  </div>
                  <p className="text-[10px] font-bold text-danger/70 leading-tight">
                    {minNotionalWarning}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-0.5 relative z-10">
                <p className="text-[9px] text-muted-foreground font-bold uppercase tracking-wider">Estimated Total Notional</p>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-black">예상 총 증거금 (사용자산)</h3>
                  {predictedTotalOpenAmount < (principalAmount * 0.95) && (
                    <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 text-[10px] font-bold animate-pulse">예산 미달</Badge>
                  )}
                  {predictedTotalOpenAmount > (principalAmount * 1.05) && (
                    <Badge variant="outline" className="bg-danger/10 text-danger border-danger/30 text-[10px] font-bold animate-pulse">예산 초과</Badge>
                  )}
                </div>
              </div>
              <div className="text-right relative z-10">
                <div className="flex flex-col items-end gap-1.5">
                  <div className="flex flex-col items-end">
                    <p className="text-3xl font-black">{predictedTotalOpenAmount.toFixed(2)} <span className="text-base font-bold opacity-70">{currency}</span></p>
                  </div>

                  <div className="flex flex-col items-end border-t border-border/40 pt-1.5 mt-1">
                    <p className="text-[10px] text-primary font-bold uppercase tracking-wide mb-0.5">총 진입 규모 (Notional)</p>
                    <p className="text-xl font-bold text-muted-foreground">{(predictedTotalOpenAmount * leverage).toFixed(2)} <span className="text-sm font-bold opacity-70">{currency}</span></p>
                  </div>

                  <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-border/40">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Info className="w-2.5 h-2.5" />
                      <span className="text-[9px] font-medium opacity-80 whitespace-nowrap">모든 단계 체결 시 총액</span>
                    </div>
                    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black tracking-tighter ${predictedTotalOpenAmount < (principalAmount * 0.95) ? 'bg-amber-500/20 text-amber-700' :
                      predictedTotalOpenAmount > (principalAmount * 1.05) ? 'bg-danger/20 text-danger' :
                        'bg-primary/10 text-primary'
                      }`}>
                      사용률 {(predictedTotalOpenAmount / (principalAmount || 1) * 100).toFixed(1)}%
                    </div>
                    {predictedTotalOpenAmount > principalAmount && (
                      <span className="text-[9px] font-bold text-danger animate-in fade-in slide-in-from-right-1 duration-300">
                        ⚠️ 예산 초과
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute -left-8 -bottom-8 w-24 h-24 bg-primary/5 blur-3xl rounded-full"></div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="bg-muted/30 p-6 flex flex-col sm:flex-row gap-4 border-t border-border">
          <Button onClick={onSave} variant="outline" className="w-full sm:flex-1 py-7 rounded-2xl border-2 border-border text-foreground font-black hover:bg-card transition-all transform active:scale-[0.98] bg-background">설정 저장</Button>
          <Button onClick={onStartBot} className="w-full sm:flex-1 py-7 rounded-2xl bg-primary text-primary-foreground font-black shadow-xl hover:opacity-90 hover:scale-[1.02] transition-all transform active:scale-[0.98]">봇 생성 미리보기</Button>
        </CardFooter>
      </Card>

      <div className="lg:col-span-4 space-y-6">
        <div className="bg-muted/40 rounded-2xl p-6 text-foreground relative group cursor-pointer shadow-sm overflow-hidden border border-border transition-colors hover:bg-muted/60">
          <div className="relative z-10">
            <p className="text-[10px] text-primary/80 font-bold uppercase tracking-widest mb-1">DCA PRO TIPS</p>
            <h3 className="text-lg font-bold mb-4 leading-tight">분할 진입 간격을 더 넓히면<br />큰 변동성에도 안전합니다.</h3>
            <div className="space-y-4">
              <div className="bg-background/50 p-3 rounded-xl border border-border">
                <p className="text-[11px] leading-relaxed opacity-90 text-muted-foreground">시장이 급락할 때 물타기가 너무 촘촘하면 증거금이 빠르게 소진될 수 있습니다. 0.75% ~ 1.5% 사이의 간격을 추천합니다.</p>
              </div>
              <Button variant="link" className="text-primary p-0 h-auto hover:opacity-80 font-bold text-sm">가이드 읽어보기 →</Button>
            </div>
          </div>
          <Zap className="absolute -right-6 -bottom-6 w-32 h-32 text-muted/20 rotate-12" />
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border shadow-sm space-y-4">
          <h4 className="text-sm font-bold text-foreground">도움말</h4>
          <ul className="space-y-3">
            {[
              { q: "레버리지 조절", a: "고배율은 청산 위험이 큽니다. 격리 마진을 권장합니다." },
              { q: "수량 배수", a: "이전 진입량 대비 다음 진입량의 배수입니다. (기본 2.0)" },
            ].map((item, idx) => (
              <li key={idx} className="space-y-1">
                <p className="text-xs font-bold text-foreground">{item.q}</p>
                <p className="text-[11px] text-muted-foreground leading-normal">{item.a}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
